import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

export const BroadcastContext = createContext();

// Rate limiting constants
const MESSAGES_FETCH_COOLDOWN = 30000; // 30 seconds between message fetches
const UNREAD_COUNT_COOLDOWN = 15000; // 15 seconds between unread count updates
const INITIAL_DELAY_MS = 3000; // 3 second delay before initial fetch

export const BroadcastProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pollingError, setPollingError] = useState(false);
  
  // Refs for rate limiting
  const intervalRef = useRef(null);
  const lastMessageFetchRef = useRef(0);
  const lastUnreadFetchRef = useRef(0);
  const fetchInProgressRef = useRef(false);
  const initialFetchDone = useRef(false);
  const abortControllerRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return {};
    }
    return { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getUserIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      return decoded.user_id || decoded.id;
    } catch (e) {
      console.error('Error decoding token:', e);
      return null;
    }
  };

  // Fetch messages with rate limiting and deduplication
  const fetchMessages = useCallback(async (force = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const now = Date.now();
    
    // Throttle: only fetch every 30 seconds unless forced
    if (!force && (now - lastMessageFetchRef.current < MESSAGES_FETCH_COOLDOWN)) {
      return; // Silently skip to prevent console spam
    }
    
    // Prevent concurrent fetches
    if (fetchInProgressRef.current) {
      return; // Silently skip to prevent console spam
    }

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    fetchInProgressRef.current = true;
    setLoading(true);
    
    try {
      const { data } = await axios.get(
        'https://api.miplapp.in/public/api/messages', 
        { 
          headers: getAuthHeaders(),
          signal: abortControllerRef.current.signal,
          timeout: 8000 // 8 second timeout
        }
      );
      
      const userId = getUserIdFromToken();
      const processed = data.map((msg) => ({
        ...msg,
        is_read: msg.users?.find(u => u.id === userId)?.is_read === 1
      }));
      
      setMessages(processed);
      setError(null);
      setPollingError(false);
      lastMessageFetchRef.current = now;
      
      // Update unread count after fetching messages
      const unread = processed.filter(m => !m.is_read).length;
      setUnreadCount(unread);
      
    } catch (err) {
      // Don't show error for cancelled requests or rate limits
      if (err.name === 'AbortError') {
        // Request cancelled safely
      } else if (err.response?.status === 429) {
        setPollingError(false); // Don't show error for rate limits
      } else {
        console.error('Error fetching messages:', err);
        setError('Failed to load messages');
        setPollingError(true);
      }
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  },[]);

  // Fetch unread count with rate limiting
  const fetchUnreadCount = useCallback(async (force = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const now = Date.now();
    
    // Throttle: only fetch every 15 seconds unless forced
    if (!force && (now - lastUnreadFetchRef.current < UNREAD_COUNT_COOLDOWN)) {
      return;
    }
    
    lastUnreadFetchRef.current = now;
    
    // Just count from existing messages if available
    if (messages.length > 0) {
      const count = messages.filter(m => !m.is_read).length;
      setUnreadCount(count);
      return;
    }
    
    // If no messages, fetch them first
    await fetchMessages(force);
  }, [messages, fetchMessages]);

  const markAsRead = useCallback(async (messageId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      await axios.put(
        `https://api.miplapp.in/public/api/messages/${messageId}/read`,
        null,
        { 
          headers: getAuthHeaders(),
          timeout: 8000
        }
      );

      // Update local state immediately
      setMessages(prev =>
        prev.map(msg => (msg.id === messageId ? { ...msg, is_read: true } : msg))
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (err) {
      if (err.response?.status !== 429) {
        console.error('Error marking message as read:', err);
      }
    }
  },[]);

  const sendBroadcast = useCallback(async (data) => {
    try {
      const formData = new FormData();
      formData.append('sender_name', data.sender_name);
      formData.append('recipient_role', data.recipient_role);
      formData.append('subject', data.subject);
      formData.append('content', data.content);
      formData.append('status', data.status);
      if (data.attachment) formData.append('attachment', data.attachment);

      const response = await axios.post(
        'https://api.miplapp.in/public/api/broadcast',
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000 // 30 second timeout for file uploads
        }
      );

      // Force refresh after sending broadcast
      await fetchMessages(true);
      return response.data;
    } catch (err) {
      console.error('Error sending broadcast:', err);
      throw err;
    }
  },[fetchMessages]);

  // Initialize with delay and watch for token changes
  useEffect(() => {
    const checkTokenAndFetch = () => {
      const token = localStorage.getItem('token');
      if (token && !initialFetchDone.current) {
        // Add initial delay to prevent multiple simultaneous requests
        const timer = setTimeout(() => {
          fetchMessages(true);
          initialFetchDone.current = true;
        }, INITIAL_DELAY_MS);
        
        return () => clearTimeout(timer);
      }
    };

    checkTokenAndFetch();

    const storageListener = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token added or changed
          initialFetchDone.current = false;
          checkTokenAndFetch();
        } else {
          // Token removed
          initialFetchDone.current = false;
          setMessages([]);
          setUnreadCount(0);
        }
      }
    };

    window.addEventListener('storage', storageListener);
    return () => {
      window.removeEventListener('storage', storageListener);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchMessages]);

  // Set up polling with increased interval
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Set up polling for unread count only (30 seconds)
    intervalRef.current = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchUnreadCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  },[]);

  const value = {
    unreadCount,
    messages,
    loading,
    error,
    pollingError,
    fetchMessages: () => fetchMessages(true), // Expose force fetch option
    markAsRead,
    sendBroadcast,
    fetchUnreadCount: () => fetchUnreadCount(true),
  };

  return (
    <BroadcastContext.Provider value={value}>
      {children}
    </BroadcastContext.Provider>
  );
};

export default BroadcastContext;